import { Box, Button, Slider, TextField, Typography } from '@mui/material';
import TempoIconLight from 'assets/images/Repertoire/TempoIconLight.svg';
import TempoPlus from 'assets/images/Repertoire/TempoPlus.png';
import TempoMinus from 'assets/images/Repertoire/TempoMinus.png';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  tempoToggleChrome,
  tempoToggleIconChrome,
  tempoToggleTextChrome,
} from '../shared/playbackChrome';

type PlaybackTempoControlProps = {
  tempo: number;
  defaultTempo: number;
  minTempo?: number;
  maxTempo?: number;
  onTempoChange: (tempo: number) => void;
};

const clampTempo = (value: number, minTempo: number, maxTempo: number) =>
  Math.max(minTempo, Math.min(maxTempo, Math.round(value)));

const sliderStyles = {
  color: '#6F87F1',
  '& .MuiSlider-track': {
    color: '#2D3A67',
  },
  '& .MuiSlider-rail': {
    color: '#2D3A67',
  },
};

const popoverStyles = {
  position: 'absolute',
  zIndex: 10,
  right: 0,
  bottom: '80px',
  minWidth: '350px',
  borderRadius: '8px',
  boxShadow:
    '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
  padding: '16px',
  backgroundColor: '#050B13',
  animation: 'fadeInSlideUp 0.2s ease-out',
  '@keyframes fadeInSlideUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
};

const titleStyles = {
  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '15px',
  fontHeight: '700',
  lineHeight: '24px',
  letterSpacing: '1px',
  textAlign: 'left',
};

const PlaybackTempoControl = ({
  tempo,
  defaultTempo,
  minTempo = 40,
  maxTempo = 220,
  onTempoChange,
}: PlaybackTempoControlProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [manualTempoInput, setManualTempoInput] = useState(tempo.toString());

  useEffect(() => {
    setManualTempoInput(tempo.toString());
  }, [tempo]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !toggleRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const sliderValue = useMemo(() => {
    if (maxTempo === minTempo) return 0;
    return ((tempo - minTempo) / (maxTempo - minTempo)) * 100;
  }, [maxTempo, minTempo, tempo]);

  const applyTempo = (value: number) => {
    onTempoChange(clampTempo(value, minTempo, maxTempo));
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {open && (
        <Box data-repertoire-create-tempo-popover sx={popoverStyles} ref={containerRef}>
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10px',
              right: '36px',
              width: '0',
              height: '0',
              borderLeft: '16px solid transparent',
              borderRight: '16px solid transparent',
              borderTop: '16px solid #050B13',
            }}
          />

          <Box sx={titleStyles}>
            <Box
              sx={{
                textAlign: 'center',
                marginTop: '6px',
                marginBottom: '16px',
                fontSize: '20px',
                position: 'relative',
              }}
            >
              TEMPO
            </Box>

            <Box
              sx={{
                textAlign: 'center',
                marginTop: '30px',
                marginBottom: '16px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
              }}
            >
              <Box
                sx={{
                  display: 'inline-block',
                  '&:hover': { cursor: 'pointer' },
                  userSelect: 'none',
                  WebkitTouchCallout: 'none',
                  touchAction: 'manipulation',
                }}
                onClick={() => applyTempo(tempo - 1)}
              >
                <img style={{ maxHeight: '28px', verticalAlign: 'top' }} src={TempoMinus} />
              </Box>
              <Box
                sx={{
                  margin: '0px 20px',
                  display: 'flex',
                  fontSize: '24px',
                  alignItems: 'start',
                }}
              >
                <Box sx={{ marginTop: '-3px', marginRight: '1px' }}>
                  <TextField
                    type="number"
                    value={manualTempoInput}
                    onChange={(event) => setManualTempoInput(event.target.value)}
                    onBlur={() => {
                      const parsed = Number.parseInt(manualTempoInput, 10);
                      applyTempo(Number.isFinite(parsed) ? parsed : tempo);
                    }}
                    inputProps={{
                      style: {
                        textAlign: 'left',
                        color: '#FFFFFF',
                        fontFamily: 'Lato',
                        fontWeight: 400,
                        fontSize: '24px',
                        lineHeight: '24px',
                        letterSpacing: '1px',
                        height: '0px',
                        margin: 0,
                        paddingLeft: '3px',
                        paddingRight: '3px',
                      },
                    }}
                    sx={{
                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                        {
                          '-webkit-appearance': 'none',
                          margin: 0,
                        },
                      '& input[type=number]': {
                        '-moz-appearance': 'textfield',
                      },
                      maxWidth: '54px',
                      marginLeft: 0,
                      padding: 0,
                    }}
                  />
                </Box>
                <Box sx={{ fontSize: '16px' }}>BPM</Box>
              </Box>
              <Box
                sx={{
                  display: 'inline-block',
                  '&:hover': { cursor: 'pointer' },
                  userSelect: 'none',
                  WebkitTouchCallout: 'none',
                  touchAction: 'manipulation',
                }}
                onClick={() => applyTempo(tempo + 1)}
              >
                <img style={{ maxHeight: '28px', verticalAlign: 'top' }} src={TempoPlus} />
              </Box>
            </Box>

            <Box
              sx={{
                textAlign: 'center',
                marginTop: '6px',
                marginBottom: '16px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ textAlign: 'center', margin: '20px 30px 10px 30px' }}>
                <Slider
                  sx={sliderStyles}
                  value={sliderValue}
                  onChange={(_event, value) => {
                    const sliderPct = Array.isArray(value) ? value[0] : value;
                    applyTempo(minTempo + (sliderPct / 100) * (maxTempo - minTempo));
                  }}
                  min={0}
                  max={100}
                  aria-labelledby="draft-tempo-slider"
                  valueLabelFormat={() => `${tempo} BPM`}
                />
                <Typography>{`${tempo} BPM`}</Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '22px',
              }}
            >
              <Button
                variant="outlined"
                onClick={() => applyTempo(defaultTempo)}
                sx={{
                  color: '#ECECED',
                  borderColor: '#525969',
                  borderRadius: '999px',
                  paddingX: '16px',
                  '&:hover': {
                    borderColor: '#6F87F1',
                    backgroundColor: 'rgba(111, 135, 241, 0.08)',
                  },
                }}
              >
                Reset to {defaultTempo} BPM
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        data-repertoire-create-tempo-toggle
        ref={toggleRef}
        sx={{ ...tempoToggleChrome, marginRight: '0px' }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <img style={tempoToggleIconChrome} src={TempoIconLight} />
        <Box sx={tempoToggleTextChrome}>
          {tempo} BPM
          <KeyboardArrowDownIcon
            style={{ stroke: 'white', width: 28, strokeWidth: 0.8, marginLeft: 8 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PlaybackTempoControl;
